import type { BackendModule, ReadCallback } from "i18next";

/**
 * The namespace react-i18next falls back to when `useTranslation()` is called
 * without an explicit namespace and the i18next instance has `defaultNS: false`
 * (our configuration). There is no `translation` namespace on disk in this
 * monorepo, so any attempt to load it is a programming mistake: a component
 * forgot to pass its namespace, e.g. `useTranslation("my-app-ts-translations")`.
 *
 * See react-i18next `useTranslation`:
 *   `const nsOrContext = ns || defaultNS || i18n.options.defaultNS;`
 *   `const namespaces = isString(nsOrContext) ? [nsOrContext] : nsOrContext || ['translation'];`
 */
export const MISSING_NAMESPACE_FALLBACK_NS = "translation";

/**
 * Creates an i18next {@link BackendModule} that guards against
 * `useTranslation()` calls that omit an explicit namespace.
 *
 * Placed at the FRONT of the chained-backend chain, it intercepts loads of the
 * fallback {@link MISSING_NAMESPACE_FALLBACK_NS} namespace and:
 *  - reports a loud error (so the mistake is noticed during development), and
 *  - short-circuits the chain by returning an empty resource bundle, so
 *    i18next never issues an HTTP request for
 *    `assets/i18n/translation/{{lng}}.json` (which 404s on a static server).
 *
 * Every real namespace is deferred to the next backend in the chain.
 *
 * @param report Invoked once per offending language. Defaults to
 *   `console.error`. Injectable for testing.
 */
export function createMissingNamespaceGuardBackend(
  report: (message: string) => void = (message) => console.error(message),
): BackendModule {
  // Report at most once per language so a re-rendering component does not
  // flood the console.
  const reported = new Set<string>();

  return {
    type: "backend",
    init: () => undefined,
    read: (language: string, namespace: string, callback: ReadCallback) => {
      if (namespace === MISSING_NAMESPACE_FALLBACK_NS) {
        if (!reported.has(language)) {
          reported.add(language);
          report(
            `i18next: a component called useTranslation() without an explicit ` +
              `namespace, so it fell back to the nonexistent ` +
              `"${MISSING_NAMESPACE_FALLBACK_NS}" namespace (language ` +
              `"${language}"). Pass an explicit namespace, e.g. ` +
              `useTranslation("my-app-ts-translations").`,
          );
        }
        // Return an empty bundle to stop the chain here and avoid the 404.
        callback(null, {});
        return;
      }
      // Defer every real namespace to the next backend in the chain.
      callback(null, null);
    },
  };
}
